import { MouseEventHandler } from 'react';
import { format, utcToZonedTime } from 'date-fns-tz';
import { makeStyles } from '@material-ui/styles';
import cn from 'clsx';

import { CheckboxCircleFill, SlideShow, TagButton, Text, LockFill } from '@bizzabo/design-system';
import { Activity } from '@bizzabo/entities';

export enum TimeButtonType {
    Toggle = 'toggle',
    ToggleFill = 'toggleFill',
    Blocked = 'blocked',
}

export type TimeButtonProps = {
    startTime: Date;
    endTime?: Date;
    timeZone?: string;
    text?: string;
    selected?: boolean;
    variant?: TimeButtonType;
    overlapActivities?: Activity[];
    className?: string;
    containerClassName?: string;
    onClickHandle?: MouseEventHandler<HTMLButtonElement>;
    openOverlapSessionsModal?: (
        e: React.MouseEvent<HTMLElement>,
        overlapActivities: Activity[]
    ) => void;
};

const TimeButton: React.FC<TimeButtonProps> = ({
    startTime,
    endTime,
    timeZone,
    text,
    variant = TimeButtonType.Toggle,
    overlapActivities = [],
    containerClassName,
    onClickHandle = () => {},
    openOverlapSessionsModal = () => {},
    ...domProps
}) => {
    const { selected, className, ...otherDomProps } = domProps;
    const classes = useStyles();
    const toggleFillVariant = selected ? 'toggleFillSelected' : 'toggleFill';
    const buttonClassNames = cn(
        classes.baseButton,
        classes[variant === TimeButtonType.ToggleFill ? toggleFillVariant : variant],
        className
    );

    const timeFromStr = format(utcToZonedTime(startTime, timeZone), endTime ? 'h:mm' : 'h:mmaaa');
    const timeToStr = endTime && format(utcToZonedTime(endTime, timeZone), 'h:mmaaa');

    return (
        <div className={cn(classes.root, containerClassName)} {...otherDomProps}>
            <TagButton className={buttonClassNames} selected={selected} onClick={onClickHandle}>
                {variant === TimeButtonType.Toggle && (
                    <CheckboxCircleFill
                        checked
                        className={selected ? classes.checkIconSelected : classes.checkIcon}
                        title="checkbox"
                    />
                )}

                <Text variant="t81" className={classes.time}>
                    {timeFromStr} {timeToStr ? `- ${timeToStr}` : null}
                </Text>

                <div className={cn(classes.detailsTextWrapper)}>
                    <Text variant="t81" className={classes.detailsText}>
                        {overlapActivities.length > 0 && variant === TimeButtonType.Toggle ? (
                            <>
                                <SlideShow className={classes.slideShowIcon} />
                                <div>
                                    Conflicts with
                                    <span
                                        className={classes.overlap}
                                        onClick={e =>
                                            openOverlapSessionsModal(e, overlapActivities)
                                        }
                                    >{` ${overlapActivities.length} ${
                                        overlapActivities.length === 1 ? 'session' : 'sessions'
                                    }`}</span>
                                </div>
                            </>
                        ) : (
                            text
                        )}
                    </Text>
                    {variant === TimeButtonType.Blocked && <LockFill />}
                </div>
            </TagButton>
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        root: {
            position: 'relative',
        },
        baseButton: {
            width: '100%',
            height: 52,
        },
        toggle: {
            color: theme.customization.background.G10,
            backgroundColor: theme.customization.background.C10,
            borderColor: theme.customization.background.G10,
            '&:hover': {
                backgroundColor: theme.customization.background.G40,
                mixBlendMode: 'initial',
            },
        },
        toggleFill: {
            color: theme.customization.background.G10,
            backgroundColor: theme.customization.background.G50,
            borderColor: theme.customization.background.G10,
            '&:hover': {
                backgroundColor: theme.customization.background.G50,
                mixBlendMode: 'initial',
            },
        },
        toggleFillSelected: {
            color: theme.customization.background.G50,
            backgroundColor: theme.customization.background.G10,
            borderColor: theme.customization.background.G10,
            '&:hover': {
                backgroundColor: theme.customization.background.G10,
                mixBlendMode: 'initial',
            },
        },
        blocked: {
            color: theme.customization.background.G40,
            borderColor: theme.customization.background.G40,
            background: `repeating-linear-gradient(
            -45deg,
            ${theme.customization.background.G30},
            ${theme.customization.background.G30} .10em,
            white 0,
            white .80em
          );`,
            '&:hover': {
                backgroundColor: theme.customization.background.G10,
                mixBlendMode: 'initial',
            },
        },
        checkIcon: {
            position: 'absolute',
            left: 20,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 20,
            height: 20,
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: theme.customization.background.G10,
            borderRadius: '50%',
            fill: theme.customization.background.G50,
            zIndex: 3,
        },
        checkIconSelected: {
            position: 'absolute',
            left: 20,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 20,
            height: 20,
            borderWidth: '0',
            borderStyle: undefined,
            borderColor: theme.customization.background.G10,
            borderRadius: '50%',
            fill: theme.customization.background.G10,
            zIndex: 3,
        },
        time: {
            lineHeight: 1,
        },
        detailsTextWrapper: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 1,
            color: '#767676',
        },
        detailsText: {
            maxWidth: '70%',
            display: 'flex',
            alignItems: 'center',
            marginRight: 4,
            lineHeight: 1,
            fontStyle: 'italic',
        },
        overlap: {
            paddingBottom: 1,
            borderBottom: 'solid 1px #767676',
            display: 'inline-block',
            marginLeft: 3,
        },
        slideShowIcon: {
            marginRight: 5,
        },
    }),
    { classNamePrefix: 'time-button' }
);

export default TimeButton;
